import type { TFunction } from 'i18next'
import { DateTime } from 'luxon'
import { z } from 'zod'

const luxonDateTime = z.preprocess((arg) => {
  if (arg instanceof DateTime) {
    return arg.toJSDate()
  }
  return arg
}, z.date())

export const getResourceSteeringDefaultsSchema = () =>
  z.object({
    defaultConsumptionLevel: z.number().nullable(),
    steeringDefaultType: z.enum(['NONE', 'SET_TO_DEFAULT_LEVEL', 'RELEASE_CONTROL']),
  })

export const getResourceSteeringMultistepSchema = () =>
  z.object({
    isMultistepResource: z.boolean(),
    multistepStrategy: z.enum(['EXCLUSIVE', 'ADDITIVE', 'UNDEFINED']).nullable(),
  })

export const getSteeringCapabilitiesSchema = () =>
  z.object({
    isReleaseControlSupported: z.boolean(),
    minReleaseControlTimeInSeconds: z.number().nullable(),
  })

export const getSteeringRangeEntrySchema = (t: TFunction) =>
  z
    .object({
      valueType: z.enum(['ABSOLUTE', 'DYNAMIC']),
      value: z.number().nullable(),
      minSecondsOnThisLevel: z.number().nullable(),
      maxSecondsOnThisLevel: z.number().nullable(),
    })
    .refine(
      (data) => {
        if (data.valueType === 'ABSOLUTE' && data.value === null) {
          return false
        }
        return true
      },
      {
        message: t('resources.steering.steering_ranges.errors.value_required_for_absolute'),
        path: ['value'],
      },
    )

export const getResourceMultistepMappingSchema = () =>
  z.object({
    stepValue: z.number().nullable(),
    controlPortId: z.string().nullable(),
  })

export const getResourceSteeringRangeSchema = (t: TFunction, usedControlPorts: Set<string>) =>
  z
    .object({
      max: getSteeringRangeEntrySchema(t),
      min: getSteeringRangeEntrySchema(t),
      step: getSteeringRangeEntrySchema(t),
      multistepMappings: z.array(getResourceMultistepMappingSchema()).optional(),
    })
    .refine(
      (data) => {
        return (
          data.max.valueType === 'ABSOLUTE' && data.min.valueType === 'ABSOLUTE' && data.step.valueType === 'ABSOLUTE'
        )
      },
      {
        message: t('resources.steering.steering_ranges.errors.all_values_must_be_absolute'),
        path: ['maxmin'],
      },
    )
    .refine(
      (data) => {
        return data.multistepMappings?.every((mapping) => mapping.controlPortId !== null)
      },
      {
        message: t('resources.steering.steering_ranges.errors.control_port_required'),
        path: ['multistepMappings'],
      },
    )
    .refine(
      (data) => {
        const controlPortOccurrences = new Map<string, number>()

        if (data.multistepMappings) {
          for (const mapping of data.multistepMappings) {
            if (mapping.controlPortId) {
              controlPortOccurrences.set(
                mapping.controlPortId,
                (controlPortOccurrences.get(mapping.controlPortId) ?? 0) + 1,
              )
            }
          }
        }

        return ![...controlPortOccurrences.entries()].some(
          ([controlPortId, count]) => count > 1 || usedControlPorts.has(controlPortId),
        )
      },
      {
        message: t('resources.steering.steering_ranges.errors.control_port_unique'),
        path: ['multistepMappings'],
      },
    )

export const getManualSteeringFormSchema = (t: TFunction) =>
  z
    .object({
      steeringCommandType: z.enum(['SET_TARGET_CONSUMPTION_LEVEL', 'RELEASE_CONTROL']),
      targetConsumptionLevel: z.number().nullable(),
      steerUntil: luxonDateTime.nullable(),
      force: z.boolean().default(false),
    })
    .refine(
      (data) => {
        if (data.steeringCommandType === 'SET_TARGET_CONSUMPTION_LEVEL' && data.targetConsumptionLevel === null) {
          return false
        }
        return true
      },
      {
        message: t('resources.steering.manual_steering.errors.target_required'),
        path: ['targetConsumptionLevel'],
      },
    )
    .refine(
      (data) => {
        if (data.steerUntil) {
          return data.steerUntil > new Date()
        }
        return true
      },
      {
        message: t('resources.steering.manual_steering.errors.future_date_required'),
        path: ['steerUntil'],
      },
    )

export const getUpsertResourceScheduleSchema = (t: TFunction) =>
  z.object({
    scheduleJson: z
      .string()
      .min(1, t('resources.scheduler.upsert_schedule_form.schedule_json_required_error'))
      .transform((val) => {
        try {
          return JSON.parse(val)
        } catch (e) {
          console.error('JSON parsing error:', e)
          throw new Error(t('resources.scheduler.upsert_schedule_form.schedule_json_parse_error'))
        }
      }),
  })

export const getBatteryUsableEnergySchema = (t: TFunction) =>
  z.object({
    userDefinedUsableEnergy: z
      .number()
      .positive(t('resources.steering.battery.usable_power.must_be_positive'))
      .nullable(),
  })

export const getMeteringRelationSchema = (t: TFunction) =>
  z.object({
    arithmeticOperation: z.string().min(1, t('resources.metering_port.relation.required')),
  })

export const getMeasureSchema = (t: TFunction) =>
  z.object({
    measure: z.string().min(1, t('resources.control_port.measure.required')),
  })

const getRampCurveEntrySchema = (t: TFunction) =>
  z.object({
    durationSinceStartMillis: z
      .number({ message: t('resources.ramp_curves.errors.duration_required') })
      .min(0, { message: t('resources.ramp_curves.errors.duration_min_zero') })
      .nullable(),
    consumption: z.number({ message: t('resources.ramp_curves.errors.consumption_required') }).nullable(),
  })

export const getRampCurveSchema = (t: TFunction) =>
  z
    .object({
      name: z
        .string({ message: t('resources.ramp_curves.errors.name_required') })
        .min(1, { message: t('resources.ramp_curves.errors.name_required') }),
      meteringPortId: z.string().nullable(),
      direction: z.enum(['UP', 'DOWN', 'UNDEFINED'], {
        message: t('resources.ramp_curves.errors.direction_required'),
      }),
      verifiedAt: z.string().nullable(),
      rampCurveEntries: z
        .array(getRampCurveEntrySchema(t))
        .min(1, { message: t('resources.ramp_curves.errors.entries_min_one') }),
    })
    .refine(
      (data) => {
        return data.rampCurveEntries.every(
          (entry) => entry.durationSinceStartMillis !== null && entry.consumption !== null,
        )
      },
      {
        message: t('resources.ramp_curves.errors.entries_complete'),
        path: ['rampCurveEntries'],
      },
    )
    .refine(
      (data) => {
        const durations = data.rampCurveEntries
          .map((entry) => entry.durationSinceStartMillis)
          .filter((d): d is number => d !== null)

        return durations.length <= 1 || durations.slice(1).every((d, i) => d > durations[i])
      },
      {
        message: t('resources.ramp_curves.errors.duration_ascending'),
        path: ['rampCurveEntries'],
      },
    )

export const getBulkImportRampCurvesSchema = (t: TFunction) =>
  z.object({
    file: z
      .any()
      .refine(Boolean, t('resources.ramp_curves.bulk_import.validation.file_required'))
      .refine((file) => file?.size > 0, t('resources.ramp_curves.bulk_import.validation.file_empty'))
      .refine(
        (file) => file?.name.endsWith('.xlsx') || file?.name.endsWith('.xls'),
        t('resources.ramp_curves.bulk_import.validation.file_type'),
      ),
  })

const percent = z.number().min(0).max(100).nullable()
const nonNegative = z.number().min(0).nullable()

export type ResourceLocalConfigSchemaOptions = {
  /**
   * Create mode requires both `maxChargePowerKw` and `maxDischargePowerKw` to be filled — battery-
   * nameplate values that have no sensible LC-168 default. Edit mode leaves this off and only the
   * paired-required rule (both or neither) applies.
   */
  requireMaxPower?: boolean
  /**
   * Create-entry mode (no existing `resources[]` entry for this resource) needs the user to pick a
   * `binding.batteryRef`. Other modes preserve the existing binding and skip this check.
   */
  requireBatteryRef?: boolean
}

export const getResourceLocalConfigSchema = (t: TFunction, options: ResourceLocalConfigSchemaOptions = {}) =>
  z
    .object({
      batteryRef: z.string().nullable(),
      fcrParticipating: z.boolean(),
      ffrParticipating: z.boolean(),
      mfrrParticipating: z.boolean(),
      afrrParticipating: z.boolean(),
      wholesaleParticipating: z.boolean(),
      maxSocPercent: percent,
      minSocPercent: percent,
      maxChargePowerKw: nonNegative,
      maxDischargePowerKw: nonNegative,
      chargeSpeedKwPerSecond: nonNegative,
      dischargeSpeedKwPerSecond: nonNegative,
      restSetpointKw: nonNegative,
      targetSocEnabled: z.boolean(),
      targetSocPercent: percent,
      targetSocSecondsHysteresis: nonNegative,
      targetSocSecondsReset: nonNegative,
      trickleChargePowerKw: nonNegative,
      trickleChargeSpeedKwPerSecond: nonNegative,
      trickleDischargePowerKw: nonNegative,
      trickleDischargeSpeedKwPerSecond: nonNegative,
      trickleRestSetpointKw: nonNegative,
      // Transformer guard
      // Market programs
      fcrnMaxKw: nonNegative,
      fcrnLowKw: nonNegative,
      fcrnLerKw: nonNegative,
      fcrDUpMaxKw: nonNegative,
      fcrDUpLowKw: nonNegative,
      fcrDDownMaxKw: nonNegative,
      fcrDDownLowKw: nonNegative,
      ffrFrequencyThreshold: z.enum(['49.5 Hz', '49.6 Hz', '49.7 Hz']).nullable(),
      ffrMinimumActivationDuration: z.enum(['5 s', '30 s']).nullable(),
      ffrMaxKw: nonNegative,
      ffrLowKw: nonNegative,
      mfrrRampTimeSeconds: nonNegative,
      mfrrUpMaxKw: nonNegative,
      mfrrUpLowKw: nonNegative,
      mfrrDownMaxKw: nonNegative,
      mfrrDownLowKw: nonNegative,
      afrrMaxKw: nonNegative,
    })
    .refine(
      (data) =>
        bothOrNeither(data.minSocPercent, data.maxSocPercent) && lessThan(data.minSocPercent, data.maxSocPercent),
      {
        message: t('resources.configuration.form.errors.min_soc_below_max'),
        path: ['minSocPercent'],
      },
    )
    .refine((data) => bothOrNeither(data.maxChargePowerKw, data.maxDischargePowerKw), {
      message: t('resources.configuration.form.errors.power_pair_required'),
      path: ['maxDischargePowerKw'],
    })
    .refine((data) => bothOrNeither(data.chargeSpeedKwPerSecond, data.dischargeSpeedKwPerSecond), {
      message: t('resources.configuration.form.errors.speed_pair_required'),
      path: ['dischargeSpeedKwPerSecond'],
    })
    // Trickle pair-completeness only matters while the section is enabled: when targetSocEnabled is
    // OFF the inputs are disabled (a red error on a disabled field would be an unfixable save-deadlock),
    // and the controller reads each rampSpeed direction independently while disabled anyway.
    .refine(
      (data) => !data.targetSocEnabled || bothOrNeither(data.trickleChargePowerKw, data.trickleDischargePowerKw),
      {
        message: t('resources.configuration.form.errors.trickle_setpoint_pair_required'),
        path: ['trickleDischargePowerKw'],
      },
    )
    .refine(
      (data) =>
        !data.targetSocEnabled ||
        bothOrNeither(data.trickleChargeSpeedKwPerSecond, data.trickleDischargeSpeedKwPerSecond),
      {
        message: t('resources.configuration.form.errors.trickle_speed_pair_required'),
        path: ['trickleDischargeSpeedKwPerSecond'],
      },
    )
    // Required in create mode. Split per-field (not one refine requiring both) so each error depends
    // only on its own value: with a resolver, RHF re-validates only the changed field on change.
    .refine((data) => !options.requireMaxPower || data.maxChargePowerKw !== null, {
      message: t('resources.configuration.form.errors.max_power_required'),
      path: ['maxChargePowerKw'],
    })
    .refine((data) => !options.requireMaxPower || data.maxDischargePowerKw !== null, {
      message: t('resources.configuration.form.errors.max_power_required'),
      path: ['maxDischargePowerKw'],
    })
    .refine((data) => !options.requireBatteryRef || (data.batteryRef !== null && data.batteryRef.length > 0), {
      message: t('resources.configuration.form.errors.battery_ref_required'),
      path: ['batteryRef'],
    })

/**
 * Validation for the target-SoC command form (LC-292). The form collects display units; unit
 * conversion happens at submit (see `utils/targetSoC`). `targetSoCPercent` (% 0..100) and
 * `validUntil` are required; the four setpoint/ramp fields are optional and non-negative.
 */
export const getTargetSoCSchema = (t: TFunction) =>
  z
    .object({
      targetSoCPercent: percent,
      maxSetpointChargeKw: nonNegative,
      maxSetpointDischargeKw: nonNegative,
      maxRampSpeedChargeKwPerS: nonNegative,
      maxRampSpeedDischargeKwPerS: nonNegative,
      validUntil: z.date().nullable(),
    })
    .refine((data) => data.targetSoCPercent !== null, {
      message: t('resources.soc_management.form.errors.target_soc_required'),
      path: ['targetSoCPercent'],
    })
    .refine((data) => data.validUntil !== null, {
      message: t('resources.soc_management.form.errors.valid_until_required'),
      path: ['validUntil'],
    })
    .refine((data) => data.validUntil === null || data.validUntil > new Date(), {
      message: t('resources.soc_management.form.errors.valid_until_future'),
      path: ['validUntil'],
    })

function bothOrNeither(a: number | null, b: number | null): boolean {
  return (a === null) === (b === null)
}

function lessThan(a: number | null, b: number | null): boolean {
  return a === null || b === null || a < b
}
